import React, { useEffect, useState, useCallback } from "react";
import { Brain, X, ChevronRight, ChevronLeft, Lightbulb, Sparkles, Loader2, Minimize2, Maximize2 } from "lucide-react";
import api from "../lib/api";

const KIND_BADGE = {
  critical_path:          { label: "CRITICAL PATH", color: "#3b82f6", glow: "rgba(59,130,246,0.18)" },
  critical_path_revision: { label: "REVISION ON CRITICAL PATH", color: "#f97316", glow: "rgba(249,115,22,0.18)" },
  ready_to_start:         { label: "READY TO START", color: "#22c55e", glow: "rgba(34,197,94,0.18)" },
  fan_out:                { label: "HIGH-LEVERAGE", color: "#8b5cf6", glow: "rgba(139,92,246,0.18)" },
  feedback_loop:          { label: "FEEDBACK LOOP", color: "#ef4444", glow: "rgba(239,68,68,0.18)" },
  quick_win:              { label: "QUICK WIN", color: "#94a3b8", glow: "rgba(148,163,184,0.18)" },
};

/**
 * Floating "Critical-Path Coach" card. Auto-shows on the Overview tab,
 * cycles through 1-3 active nudges every 8 s, and offers Open/Dismiss
 * actions. Polls /coach/next-actions every 30 s + listens to the same
 * stage-status WebSocket the rail uses (best-effort: only triggers a
 * refresh, never crashes if the socket is absent).
 */
export default function CoachCard({ projectId, onOpenStage }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [reasonExpanded, setReasonExpanded] = useState(false);

  const load = useCallback(async (bypass = false) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/coach/next-actions${bypass ? "?bypass_cache=true" : ""}`);
      setData(r.data);
      setIdx(0);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  // Soft poll every 30 s
  useEffect(() => {
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [load]);

  const nudges = data?.nudges || [];
  // Auto-rotate every 8s
  useEffect(() => {
    if (nudges.length <= 1 || minimized) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % nudges.length), 8000);
    return () => clearInterval(t);
  }, [nudges.length, minimized]);

  const dismiss = async () => {
    const cur = nudges[idx];
    if (!cur) return;
    try {
      await api.post(`/projects/${projectId}/coach/dismiss`, { nudge_id: cur.id });
      await load(true);
    } catch { /* ignore */ }
  };

  if (!projectId) return null;
  const cur = nudges[idx];
  const noNudges = !loading && !err && nudges.length === 0;

  // Position: floating bottom-right, above the "Made with Emergent" badge.
  const wrapStyle = {
    position: "fixed",
    right: 18,
    bottom: 84, // sit above the Emergent attribution badge
    width: minimized ? 220 : 360,
    maxWidth: "calc(100vw - 36px)",
    zIndex: 60,
    background: "var(--pf-surface-2)",
    border: "1px solid var(--pf-border)",
    borderRadius: 8,
    boxShadow: "0 10px 36px rgba(0,0,0,0.48)",
    color: "var(--pf-text)",
    overflow: "hidden",
    transition: "width 0.2s ease",
  };
  const badge = cur ? (KIND_BADGE[cur.kind] || KIND_BADGE.quick_win) : null;

  return (
    <div data-testid="coach-card" style={wrapStyle}>
      <button
        type="button"
        data-testid="coach-toggle-min"
        onClick={() => setMinimized((v) => !v)}
        title={minimized ? "Expand coach" : "Minimise coach"}
        style={{
          position: "absolute", top: 6, right: 6, background: "transparent",
          border: 0, color: "var(--pf-text-muted)", cursor: "pointer", padding: 4,
        }}
      >
        {minimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
      </button>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 14px", borderBottom: minimized ? "none" : "1px solid var(--pf-border)",
        background: badge ? `linear-gradient(90deg, ${badge.glow}, transparent)` : "transparent",
      }}>
        <Brain size={15} color="#8b5cf6" />
        <span className="pf-overline" style={{ flex: 1, fontSize: 10 }}>
          AI CRITICAL-PATH COACH
        </span>
        {data?.summary?.llm_refined && (
          <span title="Refined by Capexara Process Industry AI" style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: "#a78bfa" }}>
            <Sparkles size={10} /> Capexara Process Industry AI
          </span>
        )}
      </div>

      {minimized ? null : (
        <div style={{ padding: 14 }}>
          {loading && !data && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--pf-text-muted)", fontSize: 12 }}>
              <Loader2 className="pf-spin" size={13} /> Analysing project state…
            </div>
          )}
          {err && (
            <div data-testid="coach-error" className="pf-sev pf-sev-error" style={{ padding: 8, fontSize: 11 }}>
              {err}
            </div>
          )}
          {noNudges && (
            <div data-testid="coach-empty" style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--pf-text-muted)", fontSize: 12 }}>
              <Lightbulb size={13} /> Nothing urgent. Project is humming along nicely.
            </div>
          )}

          {cur && (
            <div data-testid={`coach-nudge-${cur.id}`}>
              {/* Kind badge */}
              <div style={{
                display: "inline-block",
                fontSize: 9, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1,
                padding: "2px 8px", borderRadius: 3, marginBottom: 8,
                background: badge.glow, color: badge.color, border: `1px solid ${badge.color}`,
              }}>{badge.label}</div>

              {/* Action sentence */}
              <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45, marginBottom: 6 }}>
                {cur.action}
              </div>

              {/* Reason */}
              <div style={{ fontSize: 11.5, color: "var(--pf-text-muted)", lineHeight: 1.5 }}>
                {reasonExpanded
                  ? cur.reason
                  : (cur.reason && cur.reason.length > 110 ? cur.reason.slice(0, 110) + "…" : cur.reason)}
                {cur.reason && cur.reason.length > 110 && (
                  <button
                    type="button"
                    data-testid="coach-why"
                    onClick={() => setReasonExpanded((v) => !v)}
                    style={{
                      marginLeft: 6, padding: 0, background: "transparent",
                      border: 0, color: "#7dd3fc", fontSize: 11, cursor: "pointer", textDecoration: "underline",
                    }}
                  >
                    {reasonExpanded ? "Show less" : "Why?"}
                  </button>
                )}
              </div>

              {/* Stage chip + actions */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
                <button
                  type="button"
                  data-testid="coach-open-stage"
                  onClick={() => onOpenStage?.(cur.stage_code)}
                  style={{
                    fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                    padding: "5px 10px", borderRadius: 3,
                    background: badge.glow, color: badge.color, border: `1px solid ${badge.color}`,
                    cursor: "pointer",
                  }}
                >
                  Open {cur.stage_code} →
                </button>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  data-testid="coach-dismiss"
                  onClick={dismiss}
                  title="Dismiss this nudge"
                  style={{
                    fontSize: 11, padding: "5px 10px",
                    background: "transparent", border: "1px solid var(--pf-border)",
                    color: "var(--pf-text-muted)", borderRadius: 3, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 4,
                  }}
                >
                  <X size={11} /> Dismiss
                </button>
              </div>

              {/* Pager */}
              {nudges.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, color: "var(--pf-text-muted)", fontSize: 10 }}>
                  <button
                    type="button"
                    data-testid="coach-prev"
                    onClick={() => setIdx((i) => (i - 1 + nudges.length) % nudges.length)}
                    style={pagerBtn}
                  ><ChevronLeft size={12} /></button>
                  <span data-testid="coach-pager">{idx + 1} / {nudges.length}</span>
                  <button
                    type="button"
                    data-testid="coach-next"
                    onClick={() => setIdx((i) => (i + 1) % nudges.length)}
                    style={pagerBtn}
                  ><ChevronRight size={12} /></button>
                  <span style={{ marginLeft: "auto" }}>auto-rotates</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const pagerBtn = {
  background: "transparent", border: "1px solid var(--pf-border)",
  color: "var(--pf-text-muted)", padding: 2, borderRadius: 2, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};
